// The calculation engine: parser wiring, dependency graph, incremental recalc.
//
// Ownership boundary — the engine reads the workbook and owns the *computed*
// values of formula cells. Literal values live in the workbook; formula results
// live here. Nothing else writes `values`.

import FormulaParser, { DepParser, FormulaError } from 'fast-formula-parser';
import type { Ref } from 'fast-formula-parser';
import type { CellValue, Sheet, Workbook } from '@/model/types';
import { getCell, sheetById, sheetByName, ck } from '@/model/workbook';
import { rangeToA1 } from '@/model/address';
import type { GiraffeData } from '@/data/types';
import { EMPTY_DATA } from '@/data/types';
import { makeGiFunctions, GI_FUNCTION_NAMES, type GiDiagnostic } from './fns/gi';
import { layerVisibility, sheetFilterText } from '@/model/sheetFilter';
import { makeFinanceFunctions } from './fns/finance';
import { EXCEL_UNRESOLVED_ARG_NAMES, makeExcelContextFunctions, makeExcelFunctions } from './fns/excel';
import { patchBlankCells } from './blanks';

export type NodeKey = string; // `${sheetId}|${row},${col}`

export const CIRCULAR = '#CIRC!';

export function nk(sheetId: string, row: number, col: number): NodeKey {
  return `${sheetId}|${row},${col}`;
}

export function parseNk(key: NodeKey): { sheetId: string; row: number; col: number } {
  const bar = key.lastIndexOf('|');
  const sheetId = key.slice(0, bar);
  const comma = key.indexOf(',', bar);
  return { sheetId, row: Number(key.slice(bar + 1, comma)), col: Number(key.slice(comma + 1)) };
}

interface RangeDep {
  node: NodeKey;
  sheetId: string;
  r1: number;
  c1: number;
  r2: number;
  c2: number;
}

export interface NamedRange {
  sheetId: string;
  r1: number;
  c1: number;
  r2: number;
  c2: number;
}

export interface EngineOptions {
  workbook: Workbook;
  /** Live Giraffe data. Called on every GI.* evaluation, so keep it cheap. */
  data?: () => GiraffeData;
  /**
   * Called with the sorted, deduplicated list of distinct expression
   * arguments found across every `GI.SUMEACH`/`GI.AVGEACH`/`GI.MINEACH`/
   * `GI.MAXEACH`/`GI.EACH` call in the workbook, whenever that set changes.
   * Declaring a custom measure is the side effect of a new, previously-unseen
   * expression appearing in one of these calls anywhere — see the Features
   * sheet's own custom-calc columns (data/featureTable.ts's `writeCustomCalcColumns`)
   * and their Giraffe sync (giraffe/analyticsMeasures.ts).
   *
   * Called MID-RECALCULATION, from inside `rebuildAll`/`onCellEdited`/
   * `onRangeEdited` — before they have finished their own `evaluate`. Any
   * further engine call this makes (rewriting the Areas sheet calls
   * `onRangeEdited` on it) must be deferred to a microtask, not made inline,
   * or it reenters `evaluate` while the outer call is still running.
   */
  onMeasureExpressionsChanged?: (expressions: string[]) => void;
}

/**
 * The first string-literal argument of every `GI.*EACH` call in `formula` —
 * the expression text a custom measure is declared from. A plain text scan,
 * not a parse: Excel's `""`-doubling is unescaped, everything else about the
 * surrounding formula is ignored.
 */
function measureExpressionsIn(formula: string): string[] {
  const re = /GI\.(?:SUMEACH|AVGEACH|MINEACH|MAXEACH|EACH)\s*\(\s*"((?:[^"]|"")*)"/gi;
  const out: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(formula))) out.push(m[1]!.replace(/""/g, '"'));
  return out;
}

/** Rows/cols a range covers, capped so `SUM(A:A)` cannot walk 1M cells. */
const MAX_RANGE_CELLS = 200_000;

export class Engine {
  private wb: Workbook;
  private dataFn: () => GiraffeData;

  private parser!: FormulaParser;
  private depParser!: DepParser;

  /** Computed values of formula cells only. Literals are read off the workbook. */
  private values = new Map<NodeKey, CellValue>();
  /** node -> nodes it directly reads (cell precedents only). */
  private precedents = new Map<NodeKey, Set<NodeKey>>();
  /** precedent node -> nodes that read it. */
  private dependents = new Map<NodeKey, Set<NodeKey>>();
  /**
   * Range precedents. Kept in `rangeIndex` too: rewriting a system sheet
   * touches every one of its cells, and a linear scan per cell was quadratic.
   */
  private rangeDeps: RangeDep[] = [];
  private rangeIndex = new RangeIndex();
  /** Formulas that read Giraffe data, and so must recalc when the project bakes. */
  private volatile = new Set<NodeKey>();
  /** Nodes currently part of a reference cycle. */
  private circular = new Set<NodeKey>();
  /** Formulas whose references are computed (OFFSET) — see `link`. */
  private computedRefs = new Set<NodeKey>();

  /** Defined names, e.g. live-block names. Rebuilt from the workbook. */
  private named = new Map<string, NamedRange>();

  /** Distinct GI.*EACH expression arguments last reported — see syncMeasureExpressions. */
  private measureExpressions = new Set<string>();
  /**
   * The GI.*EACH expressions each formula holds, and how many formulas hold
   * each one — kept up to date as formulas are linked and unlinked, so an
   * edit costs the cells it touched rather than a rescan of the workbook.
   */
  private expressionsByNode = new Map<NodeKey, string[]>();
  private expressionUses = new Map<string, number>();
  private onMeasureExpressionsChanged?: (expressions: string[]) => void;

  /**
   * The current set, for a caller that needs it right now rather than
   * waiting on the next change — a Features rewrite triggered by a bake or a
   * push has to materialise the same custom-calc columns `onMeasureExpressionsChanged`
   * would have reported, even though nothing about the expression SET changed.
   */
  currentMeasureExpressions(): string[] {
    return [...this.measureExpressions].sort();
  }

  /**
   * Warnings raised by each formula cell, keyed by node. Held per node rather
   * than in one list so a partial recalculation replaces exactly the warnings
   * its own cells produced and leaves every other cell's standing.
   */
  private diagnosticsByNode = new Map<NodeKey, GiDiagnostic[]>();
  /** The node being evaluated — how the GI sink attributes a warning. */
  private currentNode: NodeKey | null = null;

  /** Bumped on every recalculation so React views can re-render off one number. */
  version = 0;

  constructor(opts: EngineOptions) {
    this.wb = opts.workbook;
    this.dataFn = opts.data ?? (() => EMPTY_DATA);
    this.onMeasureExpressionsChanged = opts.onMeasureExpressionsChanged;
    this.buildParsers();
  }

  setWorkbook(wb: Workbook): void {
    this.wb = wb;
    this.rebuildAll();
  }

  setDataSource(fn: () => GiraffeData): void {
    this.dataFn = fn;
  }

  // -------------------------------------------------------------------------
  // Parser construction
  // -------------------------------------------------------------------------

  private buildParsers(): void {
    const onVariable = (name: string): Ref | null => {
      const r = this.named.get(name.toLowerCase());
      if (!r) return null;
      const sheet = sheetById(this.wb, r.sheetId);
      if (!sheet) return null;
      return { sheet: sheet.name, from: { row: r.r1, col: r.c1 }, to: { row: r.r2, col: r.c2 } };
    };

    this.parser = new FormulaParser({
      // The library's own stubs are overridden here — `functions` is merged
      // last, so MIN, MAX, SUMIFS and the rest resolve to ours.
      functions: { ...makeFinanceFunctions(), ...makeExcelFunctions() },
      functionsNeedContext: makeGiFunctions({
        data: () => this.dataFn(),
        defaultSource: () => this.wb.giSource ?? 'areas',
        sheetFilter: (name, source, d) => {
          const lower = name.toLowerCase();
          const sheet = this.wb.sheets.find((s) => s.name.toLowerCase() === lower);
          // "Visible only" resolves through the SAME GiraffeData this formula
          // is already reading — whatever the layer panel says right now.
          return sheetFilterText(sheet, d.fields(source), layerVisibility(d.visibleDrawingLayers?.bind(d)));
        },
        sink: {
          push: (d) => {
            if (!this.currentNode) return;
            const list = this.diagnosticsByNode.get(this.currentNode);
            if (list) list.push(d);
            else this.diagnosticsByNode.set(this.currentNode, [d]);
          },
        },
      }),
      onVariable,
      onCell: (ref) => this.readCell(ref.sheet, ref.row, ref.col),
      onRange: (ref) => this.readRange(ref),
    });

    /*
     * OFFSET has to see a REFERENCE, not the value behind it.
     *
     * The parser resolves every argument to a value before calling a function,
     * unless the function's name is on this list — which is how its own INDEX
     * works, and the only way `OFFSET(B2,0,0,12,1)` can hand back twelve cells
     * rather than one number. The list is a plain array on the instance; the
     * library is pinned, and this is the seam it left. CHOOSE, SUMIF and
     * AVERAGEIF ride along in the same group because the library already calls
     * those with a context — see fns/excel.ts.
     */
    Object.assign(this.parser.functions, makeExcelContextFunctions());
    this.parser.funsNeedContextAndNoDataRetrieve.push(...EXCEL_UNRESOLVED_ARG_NAMES);

    // An empty cell is "" beside text and 0 beside a number — see blanks.ts.
    patchBlankCells(this.parser);

    this.depParser = new DepParser({ onVariable });
  }

  /** A single cell's current value, as the parser wants it (undefined = blank). */
  private readCell(sheetName: string | undefined, row: number, col: number): CellValue | undefined {
    const sheet = sheetName ? sheetByName(this.wb, sheetName) : undefined;
    if (!sheet) return null;
    return this.valueAt(sheet, row, col) ?? null;
  }

  private readRange(ref: {
    sheet?: string;
    from: { row: number; col: number };
    to: { row: number; col: number };
  }): (CellValue | undefined)[][] {
    const sheet = ref.sheet ? sheetByName(this.wb, ref.sheet) : undefined;
    if (!sheet) return [[null]];
    // `SUM(A:A)` arrives as row 1..MAX_ROW. Clamp to what the sheet actually
    // uses, or the loop below walks a million empty rows per evaluation.
    const r2 = Math.min(ref.to.row, Math.max(sheet.rows, 1));
    const c2 = Math.min(ref.to.col, Math.max(sheet.cols, 1));
    const out: (CellValue | undefined)[][] = [];
    if ((r2 - ref.from.row + 1) * (c2 - ref.from.col + 1) > MAX_RANGE_CELLS) return [[null]];
    for (let r = ref.from.row; r <= r2; r++) {
      const row: (CellValue | undefined)[] = [];
      for (let c = ref.from.col; c <= c2; c++) row.push(this.valueAt(sheet, r, c) ?? null);
      out.push(row);
    }
    return out.length > 0 ? out : [[null]];
  }

  // -------------------------------------------------------------------------
  // Value access
  // -------------------------------------------------------------------------

  /** The displayable value of a cell: computed result for formulas, literal otherwise. */
  valueAt(sheet: Sheet, row: number, col: number): CellValue | undefined {
    const cell = getCell(sheet, row, col);
    if (!cell) return undefined;
    if (cell.f !== undefined) return this.values.get(nk(sheet.id, row, col)) ?? null;
    return cell.v;
  }

  valueOf(sheetId: string, row: number, col: number): CellValue | undefined {
    const sheet = sheetById(this.wb, sheetId);
    return sheet ? this.valueAt(sheet, row, col) : undefined;
  }

  /** True when the cell's value is a spreadsheet error string. */
  static isError(v: CellValue | undefined): boolean {
    return typeof v === 'string' && ERROR_RE.test(v);
  }

  // -------------------------------------------------------------------------
  // Named ranges
  // -------------------------------------------------------------------------

  setNamedRange(name: string, range: NamedRange | null): void {
    const key = name.toLowerCase();
    if (range) this.named.set(key, range);
    else this.named.delete(key);
  }

  namedRanges(): ReadonlyMap<string, NamedRange> {
    return this.named;
  }

  // -------------------------------------------------------------------------
  // Graph maintenance
  // -------------------------------------------------------------------------

  private clearGraph(): void {
    this.precedents.clear();
    this.dependents.clear();
    this.rangeDeps = [];
    this.rangeIndex.clear();
    this.volatile.clear();
    this.computedRefs.clear();
    this.circular.clear();
    this.diagnosticsByNode.clear();
    this.expressionsByNode.clear();
    this.expressionUses.clear();
  }

  private forgetExpressions(node: NodeKey): void {
    const held = this.expressionsByNode.get(node);
    if (!held) return;
    this.expressionsByNode.delete(node);
    for (const expr of held) {
      const n = (this.expressionUses.get(expr) ?? 0) - 1;
      if (n > 0) this.expressionUses.set(expr, n);
      else this.expressionUses.delete(expr);
    }
  }

  private noteExpressions(node: NodeKey, formula: string): void {
    this.forgetExpressions(node);
    const held = [...new Set(measureExpressionsIn(formula))];
    if (held.length === 0) return;
    this.expressionsByNode.set(node, held);
    for (const expr of held) this.expressionUses.set(expr, (this.expressionUses.get(expr) ?? 0) + 1);
  }

  /** Every warning currently standing across the workbook. */
  diagnostics(): GiDiagnostic[] {
    const out: GiDiagnostic[] = [];
    for (const list of this.diagnosticsByNode.values()) out.push(...list);
    return out;
  }

  diagnosticsAt(sheetId: string, row: number, col: number): GiDiagnostic[] {
    return this.diagnosticsByNode.get(nk(sheetId, row, col)) ?? [];
  }

  private unlink(node: NodeKey): void {
    const prev = this.precedents.get(node);
    if (prev) {
      for (const p of prev) this.dependents.get(p)?.delete(node);
      this.precedents.delete(node);
    }
    if (this.rangeIndex.dropNode(node)) {
      this.rangeDeps = this.rangeDeps.filter((rd) => rd.node !== node);
    }
    this.volatile.delete(node);
    this.computedRefs.delete(node);
    this.diagnosticsByNode.delete(node);
    this.forgetExpressions(node);
  }

  /**
   * Re-derive one formula cell's precedents. Cell refs become graph edges;
   * range refs go on the linear scan list, because expanding `A1:C10000` into
   * 30,000 edges is how a spreadsheet engine runs out of memory.
   */
  private link(sheet: Sheet, row: number, col: number, formula: string): void {
    const node = nk(sheet.id, row, col);
    this.noteExpressions(node, formula);
    const pos = { sheet: sheet.name, row, col };
    let refs: Ref[] = [];
    try {
      refs = this.depParser.parse(formula, pos);
    } catch {
      // Unparseable — no dependencies to record. The error surfaces at eval.
      refs = [];
    }

    const cellSet = new Set<NodeKey>();
    for (const ref of refs) {
      const targetSheet = ref.sheet ? sheetByName(this.wb, ref.sheet) : sheet;
      if (!targetSheet) continue;
      if ('from' in ref) {
        const rd: RangeDep = {
          node,
          sheetId: targetSheet.id,
          r1: ref.from.row,
          c1: ref.from.col,
          r2: ref.to.row,
          c2: ref.to.col,
        };
        this.rangeDeps.push(rd);
        this.rangeIndex.add(rd);
      } else {
        cellSet.add(nk(targetSheet.id, ref.row, ref.col));
      }
    }

    this.precedents.set(node, cellSet);
    for (const p of cellSet) {
      let set = this.dependents.get(p);
      if (!set) {
        set = new Set();
        this.dependents.set(p, set);
      }
      set.add(node);
    }

    if (containsGiCall(formula)) this.volatile.add(node);
    /*
     * A formula that BUILDS its own reference has dependencies the dep parser
     * cannot see: `OFFSET(B2,0,n)` reads a cell chosen at evaluation time, and
     * nothing in the text says which. So it is re-run on every edit rather
     * than only when what it visibly names changes — Excel calls these
     * volatile for the same reason. There are a handful per workbook, if any.
     */
    if (REF_BUILDING_RE.test(formula)) this.computedRefs.add(node);
  }

  /** Every formula node that reads the given cell, directly or through a range. */
  private directDependents(sheetId: string, row: number, col: number): NodeKey[] {
    const out: NodeKey[] = [];
    const direct = this.dependents.get(nk(sheetId, row, col));
    if (direct) out.push(...direct);
    this.rangeIndex.covering(sheetId, row, col, out);
    return out;
  }

  // -------------------------------------------------------------------------
  // Recalculation
  // -------------------------------------------------------------------------

  /** Rebuild the whole graph and evaluate every formula. */
  rebuildAll(): void {
    this.clearGraph();
    this.values.clear();
    this.syncNamedRanges();
    const nodes: NodeKey[] = [];
    for (const sheet of this.wb.sheets) {
      for (const [key, cell] of Object.entries(sheet.cells)) {
        if (cell.f === undefined) continue;
        const comma = key.indexOf(',');
        const row = Number(key.slice(0, comma));
        const col = Number(key.slice(comma + 1));
        this.link(sheet, row, col, cell.f);
        nodes.push(nk(sheet.id, row, col));
      }
    }
    this.syncMeasureExpressions();
    this.evaluate(nodes);
  }

  /** A cell was edited. Re-link if it is a formula, then recalc what depends on it. */
  onCellEdited(sheetId: string, row: number, col: number): void {
    const sheet = sheetById(this.wb, sheetId);
    if (!sheet) return;
    const node = nk(sheetId, row, col);
    this.unlink(node);
    this.values.delete(node);
    const cell = getCell(sheet, row, col);
    const seeds: NodeKey[] = [];
    if (cell?.f !== undefined) {
      this.link(sheet, row, col, cell.f);
      seeds.push(node);
    }
    seeds.push(...this.directDependents(sheetId, row, col));
    seeds.push(...this.computedRefs);
    this.syncMeasureExpressions();
    this.evaluate(seeds);
  }

  /** A block of cells changed at once (paste, live-block refresh, clear). */
  onRangeEdited(sheetId: string, r1: number, c1: number, r2: number, c2: number): void {
    const sheet = sheetById(this.wb, sheetId);
    if (!sheet) return;
    const seeds: NodeKey[] = [];
    for (let r = r1; r <= r2; r++) {
      for (let c = c1; c <= c2; c++) {
        const node = nk(sheetId, r, c);
        this.unlink(node);
        this.values.delete(node);
        const cell = getCell(sheet, r, c);
        if (cell?.f !== undefined) {
          this.link(sheet, r, c, cell.f);
          seeds.push(node);
        }
        const direct = this.dependents.get(node);
        if (direct) seeds.push(...direct);
      }
    }
    // Range readers once for the whole block, not once per cell.
    this.rangeIndex.overlapping(sheetId, r1, c1, r2, c2, seeds);
    seeds.push(...this.computedRefs);
    this.syncMeasureExpressions();
    this.evaluate(seeds);
  }

  /**
   * The project baked, or the selection changed. Only formulas that actually
   * call GI.* are stale — recalculating the whole workbook on every map nudge
   * is the difference between a live sheet and an unusable one.
   */
  onDataChanged(): void {
    if (this.volatile.size === 0) {
      this.version += 1;
      return;
    }
    this.evaluate([...this.volatile]);
  }

  hasVolatileCells(): boolean {
    return this.volatile.size > 0;
  }

  /**
   * Evaluate the seeds and everything downstream, in dependency order.
   *
   * Order comes from a DFS over the dependent edges collecting a reverse
   * postorder. Nodes reached while already on the stack form a cycle: they get
   * #CIRC! and are excluded from evaluation, rather than recursing forever.
   */
  private evaluate(seeds: readonly NodeKey[]): void {
    if (seeds.length === 0) {
      this.version += 1;
      return;
    }

    const order: NodeKey[] = [];
    const state = new Map<NodeKey, 0 | 1 | 2>(); // 0 unseen, 1 on stack, 2 done
    const cycle = new Set<NodeKey>();

    const visit = (node: NodeKey, stack: NodeKey[]): void => {
      const s = state.get(node);
      if (s === 2) return;
      if (s === 1) {
        // Everything from this node's position on the stack is in the cycle.
        const at = stack.lastIndexOf(node);
        for (let i = at >= 0 ? at : 0; i < stack.length; i++) cycle.add(stack[i]!);
        cycle.add(node);
        return;
      }
      state.set(node, 1);
      stack.push(node);
      const { sheetId, row, col } = parseNk(node);
      for (const dep of this.directDependents(sheetId, row, col)) visit(dep, stack);
      stack.pop();
      state.set(node, 2);
      order.push(node);
    };

    for (const seed of seeds) visit(seed, []);
    order.reverse(); // precedents first

    for (const node of order) {
      if (cycle.has(node)) {
        this.circular.add(node);
        this.values.set(node, CIRCULAR);
        continue;
      }
      this.circular.delete(node);
      this.diagnosticsByNode.delete(node);
      this.currentNode = node;
      this.values.set(node, this.computeOne(node));
      this.currentNode = null;
    }
    for (const node of cycle) {
      this.circular.add(node);
      this.values.set(node, CIRCULAR);
    }

    this.version += 1;
  }

  private computeOne(node: NodeKey): CellValue {
    const { sheetId, row, col } = parseNk(node);
    const sheet = sheetById(this.wb, sheetId);
    if (!sheet) return null;
    const cell = getCell(sheet, row, col);
    if (!cell || cell.f === undefined) return null;
    return this.evaluateFormula(cell.f, sheet.name, row, col);
  }

  /**
   * One formula, one result. Three failure shapes have to be flattened here:
   * a returned FormulaError (`1/0`), a thrown FormulaError (syntax errors and
   * our own custom functions), and a thrown plain Error (a bug in a custom
   * function — it must not take the whole recalculation down).
   */
  evaluateFormula(formula: string, sheetName: string, row: number, col: number): CellValue {
    let result: unknown;
    try {
      result = this.parser.parse(formula, { sheet: sheetName, row, col });
    } catch (err) {
      return errorText(err);
    }
    if (result instanceof FormulaError) return errorText(result);
    if (result === null || result === undefined) return 0; // Excel: blank ref reads as 0
    if (typeof result === 'number') return Number.isFinite(result) ? result : '#NUM!';
    if (typeof result === 'string' || typeof result === 'boolean') return result;
    if (Array.isArray(result)) {
      // No spill in v1 — take the top-left, as Excel's implicit intersection does.
      const first = (result as unknown[][])[0]?.[0];
      if (typeof first === 'number' || typeof first === 'string' || typeof first === 'boolean') return first;
      return null;
    }
    return String(result);
  }

  isCircular(sheetId: string, row: number, col: number): boolean {
    return this.circular.has(nk(sheetId, row, col));
  }

  /**
   * Report the distinct `GI.*EACH` expression arguments when they have
   * changed. Called alongside every recalculation path (`rebuildAll`,
   * `onCellEdited`, `onRangeEdited`) rather than only on a full rebuild: a
   * newly-typed `GI.SUMEACH("...")` has to reach the Features tab as soon as
   * it is typed, the same way its own value appears. The set itself is kept
   * by `link`/`unlink` (see `expressionUses`).
   */
  private syncMeasureExpressions(): void {
    if (!this.onMeasureExpressionsChanged) return;
    const next = new Set(this.expressionUses.keys());
    if (next.size === this.measureExpressions.size && [...next].every((e) => this.measureExpressions.has(e))) return;
    this.measureExpressions = next;
    this.onMeasureExpressionsChanged([...next].sort());
  }

  /**
   * Mirror live-block extents into defined names so `=SUM(Schedule1)` works,
   * alongside any named ranges an .xlsx import carried in (`wb.namedRanges` —
   * see xlsxImport.ts). A block wins over an imported name that collides with
   * it: blocks are the live, user-managed names, an import is a frozen
   * leftover from wherever the file came from.
   */
  syncNamedRanges(): void {
    this.named.clear();
    for (const [name, range] of Object.entries(this.wb.namedRanges ?? {})) {
      this.named.set(name.toLowerCase(), range);
    }
    for (const block of Object.values(this.wb.blocks)) {
      const headerRows = block.showHeader ? 1 : 0;
      this.named.set(block.name.toLowerCase(), {
        sheetId: block.sheetId,
        r1: block.anchor.row + headerRows,
        c1: block.anchor.col,
        r2: block.anchor.row + headerRows + block.rowBudget - 1,
        c2: block.anchor.col + block.measures.length,
      });
    }
  }

  /** Debug helper — the A1 refs a formula cell reads. Same-sheet refs unqualified. */
  precedentsOf(sheetId: string, row: number, col: number): string[] {
    const node = nk(sheetId, row, col);
    const nameOf = (id: string): string | undefined =>
      id === sheetId ? undefined : sheetById(this.wb, id)?.name;
    const out: string[] = [];
    for (const p of this.precedents.get(node) ?? []) {
      const parsed = parseNk(p);
      out.push(rangeToA1({ r1: parsed.row, c1: parsed.col, r2: parsed.row, c2: parsed.col, sheet: nameOf(parsed.sheetId) }));
    }
    for (const rd of this.rangeDeps) {
      if (rd.node !== node) continue;
      out.push(rangeToA1({ r1: rd.r1, c1: rd.c1, r2: rd.r2, c2: rd.c2, sheet: nameOf(rd.sheetId) }));
    }
    return out;
  }

  /** Used by tests and the status bar. */
  stats(): { formulas: number; volatile: number; circular: number; ranges: number } {
    return {
      formulas: this.precedents.size,
      volatile: this.volatile.size,
      circular: this.circular.size,
      ranges: this.rangeDeps.length,
    };
  }
}

// ---------------------------------------------------------------------------

/**
 * Range precedents bucketed by sheet, then by column. A range wider than
 * `WIDE` columns (a whole-row ref) goes on the sheet's short wide list rather
 * than into every column bucket.
 */
const WIDE = 64;
class RangeIndex {
  private bySheet = new Map<string, { cols: Map<number, RangeDep[]>; wide: RangeDep[] }>();
  /** Nodes with at least one range precedent — most cells have none. */
  private nodes = new Map<NodeKey, RangeDep[]>();

  clear(): void {
    this.bySheet.clear();
    this.nodes.clear();
  }

  add(rd: RangeDep): void {
    let sheet = this.bySheet.get(rd.sheetId);
    if (!sheet) {
      sheet = { cols: new Map(), wide: [] };
      this.bySheet.set(rd.sheetId, sheet);
    }
    if (rd.c2 - rd.c1 >= WIDE) sheet.wide.push(rd);
    else {
      for (let c = rd.c1; c <= rd.c2; c++) {
        const list = sheet.cols.get(c);
        if (list) list.push(rd);
        else sheet.cols.set(c, [rd]);
      }
    }
    const held = this.nodes.get(rd.node);
    if (held) held.push(rd);
    else this.nodes.set(rd.node, [rd]);
  }

  /** Remove every range `node` reads. False when it read none (nothing to do). */
  dropNode(node: NodeKey): boolean {
    const held = this.nodes.get(node);
    if (!held) return false;
    this.nodes.delete(node);
    for (const rd of held) {
      const sheet = this.bySheet.get(rd.sheetId);
      if (!sheet) continue;
      if (rd.c2 - rd.c1 >= WIDE) sheet.wide = sheet.wide.filter((x) => x !== rd);
      else {
        for (let c = rd.c1; c <= rd.c2; c++) {
          const list = sheet.cols.get(c);
          if (!list) continue;
          const i = list.indexOf(rd);
          if (i >= 0) list.splice(i, 1);
          if (list.length === 0) sheet.cols.delete(c);
        }
      }
    }
    return true;
  }

  /** Push the node of every range containing (row, col). */
  covering(sheetId: string, row: number, col: number, out: NodeKey[]): void {
    const sheet = this.bySheet.get(sheetId);
    if (!sheet) return;
    for (const rd of sheet.cols.get(col) ?? []) if (row >= rd.r1 && row <= rd.r2) out.push(rd.node);
    for (const rd of sheet.wide) {
      if (row >= rd.r1 && row <= rd.r2 && col >= rd.c1 && col <= rd.c2) out.push(rd.node);
    }
  }

  /** Push the node of every range overlapping the block, once each. */
  overlapping(sheetId: string, r1: number, c1: number, r2: number, c2: number, out: NodeKey[]): void {
    const sheet = this.bySheet.get(sheetId);
    if (!sheet) return;
    const seen = new Set<RangeDep>();
    const hit = (rd: RangeDep): void => {
      if (seen.has(rd)) return;
      seen.add(rd);
      if (rd.r1 <= r2 && rd.r2 >= r1 && rd.c1 <= c2 && rd.c2 >= c1) out.push(rd.node);
    };
    if (c2 - c1 < sheet.cols.size) {
      for (let c = c1; c <= c2; c++) for (const rd of sheet.cols.get(c) ?? []) hit(rd);
    } else {
      for (const list of sheet.cols.values()) for (const rd of list) hit(rd);
    }
    for (const rd of sheet.wide) hit(rd);
  }
}

function errorText(err: unknown): string {
  if (err instanceof FormulaError) {
    const code = (err as unknown as { _error?: string })._error ?? '#ERROR!';
    return code;
  }
  if (err instanceof Error) return '#ERROR!';
  return '#ERROR!';
}

const ERROR_RE = /^#[A-Z0-9/]+[!?]$/;

/** Functions that work out at evaluation time which cells they read. */
const REF_BUILDING_RE = /\b(OFFSET|INDIRECT)\s*\(/i;

const GI_CALL_RE = new RegExp(`\\b(${GI_FUNCTION_NAMES.map((n) => n.replace('.', '\\.')).join('|')})\\s*\\(`, 'i');

export function containsGiCall(formula: string): boolean {
  return GI_CALL_RE.test(formula);
}

export { ck };
export type { GiDiagnostic };
